import { gsap } from "gsap";

/**
 * Next Project Scroll & Click Motion Transition
 * 
 * Features:
 * - Smooth upward glide motion when clicking "Next project"
 * - Automatic motion transition when scrolling past the bottom of the page
 * - Touch swipe support at bottom of page
 * - Arrival curtain on the next project so the change of project is noticeable
 */

const TRANSITION_KEY = 'project-transition';

const markTransition = () => {
   try { sessionStorage.setItem(TRANSITION_KEY, '1'); } catch (e) { /* storage unavailable */ }
};

const consumeTransition = () => {
   try {
      const flag = sessionStorage.getItem(TRANSITION_KEY) === '1';
      sessionStorage.removeItem(TRANSITION_KEY);
      return flag;
   } catch (e) {
      return false;
   }
};

// Reveals the new project: the arrival curtain keeps gliding upwards and uncovers the page
function initProjectArrival() {
   const enterCurtain = document.querySelector('.js-project-curtain-enter');
   const arrived = consumeTransition();
   if (!enterCurtain || !enterCurtain.classList.contains('is-active')) return;

   if (!arrived) {
      enterCurtain.classList.remove('is-active');
      return;
   }

   requestAnimationFrame(() => {
      setTimeout(() => {
         enterCurtain.addEventListener('transitionend', () => {
            enterCurtain.classList.remove('is-active', 'is-leaving');
         }, { once: true });
         enterCurtain.classList.add('is-leaving');
      }, 450);
   });
}

export function initProjectNext() {
   initProjectArrival();

   const nextSection = document.querySelector('.js-project-next');
   if (!nextSection) return;

   const nextUrl = nextSection.dataset.nextUrl;
   if (!nextUrl) return;

   const nextLink = nextSection.querySelector('.js-project-next-link');
   const curtain = document.querySelector('.js-project-curtain');

   let isTransitioning = false;

   function triggerTransition() {
      if (isTransitioning) return;
      isTransitioning = true;

      document.dispatchEvent(new CustomEvent('project:leave'));
      markTransition();

      if (curtain) {
         // Silky upward curtain glide transition
         gsap.to(curtain, {
            y: "0%",
            duration: 0.65,
            ease: "power3.inOut",
            onComplete: () => {
               window.location.href = nextUrl;
            }
         });
      } else {
         window.location.href = nextUrl;
      }
   }

   // Back/forward cache: restore the page if the user comes back after leaving
   window.addEventListener('pageshow', (e) => {
      if (!e.persisted) return;
      isTransitioning = false;
      if (curtain) gsap.set(curtain, { y: "100%" });
   });

   // 1. Click transition
   if (nextLink) {
      nextLink.addEventListener('click', (e) => {
         e.preventDefault();
         triggerTransition();
      });
   }

   // 2. Scroll trigger at the bottom of the page
   // If user has reached the bottom of the page and scrolls down
   let scrollThresholdCount = 0;
   let lastWheelTime = 0;

   function isAtBottom() {
      const scrollPos = window.innerHeight + window.scrollY;
      const docHeight = document.documentElement.scrollHeight;
      // Within 80px of bottom
      return scrollPos >= docHeight - 80;
   }

   window.addEventListener('wheel', (e) => {
      if (isTransitioning) {
         e.preventDefault();
         return;
      }

      if (e.deltaY > 20 && isAtBottom()) {
         const now = Date.now();
         if (now - lastWheelTime > 1500) {
            scrollThresholdCount = 0;
         }
         lastWheelTime = now;
         scrollThresholdCount++;

         // Require 2 intentional scroll down ticks when at the bottom
         if (scrollThresholdCount >= 2) {
            e.preventDefault();
            triggerTransition();
         }
      }
   }, { passive: false });

   // 3. Touch swipe at bottom on mobile
   let touchStartY = 0;
   window.addEventListener('touchstart', (e) => {
      if (e.touches && e.touches.length === 1) {
         touchStartY = e.touches[0].clientY;
      }
   }, { passive: true });

   window.addEventListener('touchend', (e) => {
      if (isTransitioning) return;
      if (!isAtBottom()) return;

      if (e.changedTouches && e.changedTouches.length === 1) {
         const touchEndY = e.changedTouches[0].clientY;
         const diffY = touchStartY - touchEndY;
         // Swipe up > 60px when at bottom
         if (diffY > 60) {
            triggerTransition();
         }
      }
   }, { passive: true });
}
